import React, { useEffect, useState } from 'react';
import { itemsPerPage as defaultItemsPerPage } from '#config';
import { useGetTeaserCollectionQuery } from '#types/generated/apollo-client';
import {
  SortableFields,
  SortDirection,
  Teaser,
} from '#types/generated/graphql-types';
import PageableGrid from '#components/grid-block/PageableGrid';
import { ITabItem } from '#components/navigation/tab-navigator/TabNavigator';

interface FilterItem extends ITabItem {
  value: {
    [key: string]: string | number | boolean | string[];
  };
}

type Props = {
  itemsPerPage?: number;
  options?: FilterItem[];
  sortField?: SortableFields;
  sortDirection?: SortDirection;
  selectedOption?: FilterItem;
};

const TeaserGridWithFilter = ({
  sortField,
  sortDirection,
  options,
  selectedOption,
  itemsPerPage = defaultItemsPerPage,
}: Props) => {
  const [hasMore, setHasMore] = useState(false);
  const [items, setItems] = useState<Teaser[]>([]);
  const [selOption, setSelOption] = useState<FilterItem | undefined>(
    selectedOption
  );
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const { data, error } = useGetTeaserCollectionQuery({
    variables: {
      params: {
        ...(sortField && {
          sort: {
            field: sortField,
            direction: sortDirection || SortDirection.Asc,
          },
        }),
        ...(selOption && selOption.value),
        page,
        itemsPerPage,
      },
    },
    ssr: false,
  });

  useEffect(() => {
    if (data) {
      const newItems = [...items];
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      newItems.splice(
        startIndex,
        endIndex,
        ...(data.teaserCollection?.items ?? [])
      );
      setItems(newItems);
      setHasMore(data?.teaserCollection?.hasMore);
      setIsLoading(false);
    }
  }, [data]);

  const handleTabChange = (item) => {
    setSelOption(item);
    setPage(1);
    setItems([]);
    setIsLoading(true);
  };

  const loadMore = () => {
    if (data?.teaserCollection?.hasMore) {
      setPage(page + 1);
    }
  };

  return (
    <PageableGrid
      options={options}
      items={items}
      loading={isLoading}
      hasMore={hasMore}
      error={error}
      selectedOption={selOption}
      onLoadMore={loadMore}
      onSelectOption={handleTabChange}
    />
  );
};

export default TeaserGridWithFilter;
